import { useEffect, useState, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { fetchRequestUsers, fetchEntities, saveRequest } from "./utils";
import { REQUEST_TYPES } from "../../useRequest";
import useFileUpload from "~/utils/useFileUpload";
import { API_ROUTES } from "~/api-routes";
import apiService from "~/services/service";
import z from "zod";
import { FEATURE_FLAGS, useFeatureFlag } from "~/config/ff";

const useRequestModal = (hideRequestModal, requestType, parentTab) => {
  const [reason, setReason] = useState(null);
  const [description, setDescription] = useState("");
  const [entity, setEntity] = useState(null);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [serviceLine, setServiceLine] = useState(null);
  const [assignee, setAssignee] = useState(null);
  const [cc, setCC] = useState([]);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [serviceLineData, setServiceLineData] = useState([]);
  const ccRef = useRef(null);
  const { role: userRole, id: loggedInUserId } = useSelector(
    (state) => state.login,
  );
  const [ccUsersData, setCCUsersData] = useState([]);
  const [allEntities, setAllEntities] = useState([]);
  const [allProcesses, setAllProcesses] = useState([]);
  const [assigneeUsers, setAssigneeUsers] = useState([]);
  const [requestId, setRequestId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [files, setFiles] = useState([]);
  const { processdata } = useSelector((state) => state.process);
  const { id: userId } = useSelector((state) => state.login);
  const {
    uploadFile,
    updateTag,
    uuidToUlid,
    SERVICE_CODES,
    REQUEST_REASON_CODES,
    getFileVersion,
  } = useFileUpload();
  const [showAttachmentConfirmation, setShowAttachmentConfirmation] =
    useState(false);
  const [payrollPlusProcess, setPayrollPlusProcess] = useState("");
  const process =
    parentTab === REQUEST_TYPES.ESCALATION ? selectedProcess : processdata;
  const { value: isImplementationRequestEnabled } = useFeatureFlag(
    FEATURE_FLAGS.IMPLEMENTATION_REQUEST,
  );
  useEffect(() => {
    if (
      requestType === REQUEST_TYPES.AD_HOC ||
      parentTab === REQUEST_TYPES.ESCALATION
    ) {
      fetchEntitiesData();
    }
  }, []);

  useEffect(() => {
    if (
      process &&
      requestType === REQUEST_TYPES.ESCALATION &&
      parentTab !== REQUEST_TYPES.ESCALATION
    ) {
      fetchAssigneeUsers(process.entity, process.service);
      fetchCCUser(process.entity, process.service);
    }
  }, [process]);

  const config = useMemo(() => {
    if (requestType === REQUEST_TYPES.AD_HOC) {
      return {
        isSendButtonDisabled:
          isLoading ||
          !description ||
          !reason ||
          !entity ||
          !serviceLine ||
          !assignee,
        cancelBackButtonLabel: "Back to Request",
        modalHeader: "New Request",
        modalDescription:
          "Your request will be automatically assigned to our team. We'll get back to you within the next 3 business days.",
        descriptionFieldLabel: "Description *",
        detailsSectionHeader: "Request Details",
        cancelModalHeader: "Are you sure you want to discard this request?",
        cancelModalDescription:
          "You have unsent changes. If you close this now, your message will be lost. Do you want to discard this message?",
        successModalHeader: "Thanks! We got your request",
        successModalDescription:
          "Your request will be automatically assigned to our team. We'll get back to you within the next 3 business days",
      };
    } else if (requestType === REQUEST_TYPES.ESCALATION) {
      return {
        isSendButtonDisabled:
          parentTab === REQUEST_TYPES.ESCALATION
            ? isLoading ||
              !description ||
              !entity ||
              !serviceLine ||
              (serviceLine?.name !== "Payroll +" && !selectedProcess) ||
              (serviceLine?.name === "Payroll +" && !payrollPlusProcess) ||
              !assignee
            : isLoading || !description || !assignee,
        cancelBackButtonLabel: "Back to Escalation",
        modalHeader:
          parentTab === REQUEST_TYPES.ESCALATION
            ? "New Escalation"
            : "Escalate Process",
        modalDescription:
          parentTab === REQUEST_TYPES.ESCALATION
            ? "Our team will take care of it and get back to you within the next business day"
            : "Your escalation will be automatically assigned to our team. We'll get back to you by the next business day",
        descriptionFieldLabel: "Description *",
        detailsSectionHeader: "Escalation Details",
        cancelModalHeader: "Are you sure you want to discard this escalation?",
        cancelModalDescription:
          "Your escalation has not been sent. If you close this now, your message will be lost. Do you want to discard this message?",
        successModalHeader: "Thanks! We got your escalation",
        successModalDescription:
          parentTab === REQUEST_TYPES.ESCALATION
            ? "The issue has been logged and our team will get back to you within the next business day."
            : "Your escalation will be automatically assigned to our team. We'll get back to you by the next business day",
      };
    }
  }, [
    requestType,
    description,
    assignee,
    reason,
    entity,
    serviceLine,
    selectedProcess,
    payrollPlusProcess,
    isLoading,
    parentTab,
  ]);

  const handleReasonChange = async (e) => {
    setReason(e);
    if (allEntities.length === 1) {
      const availableEntity = allEntities[0];
      setEntity(availableEntity);
      setServiceLineData(availableEntity.services);
      if (availableEntity.services.length === 1) {
        setServiceLine(availableEntity.services[0]);
        await fetchAssigneeUsers(
          availableEntity.id,
          availableEntity.services[0].id,
          e,
        );
      }
      await fetchCCUser(availableEntity.id, availableEntity.services[0].id);
    } else {
      setEntity(null);
      setDescription("");
      setAssignee(null);
      setServiceLine(null);
      setCC(null);
      fetchEntitiesData(e);
    }
  };

  const fetchAssigneeUsers = async (
    selectedEntityId = "",
    selectedServiceLineId = "",
    requestReason = reason,
  ) => {
    try {
      let role = "";

      if (requestType === REQUEST_TYPES.AD_HOC) {
        role =
          requestReason.code === "SERVICE_QUERY"
            ? "LSP_USER,LSP_MANAGER"
            : requestReason.code === "IMPLEMENTATION"
              ? "CUSTOMER,CUSTOMER_MANAGER"
              : "GXM_ADMIN,GXM_MANAGER,GXM_USER";
      } else if (requestType === REQUEST_TYPES.ESCALATION) {
        role = "GXM_ADMIN,GXM_MANAGER,GXM_USER";
      }
      const { users } = await fetchRequestUsers(
        role,
        selectedEntityId,
        selectedServiceLineId,
      );
      const filteredUsers = users.filter((user) => user.id !== loggedInUserId);
      if (filteredUsers.length === 1) {
        setAssignee(filteredUsers[0]);
      }
      setAssigneeUsers(filteredUsers);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCCUser = async (
    selectedEntityId = "",
    selectedServiceLineId = "",
  ) => {
    try {
      let role = "";
      if (requestType === REQUEST_TYPES.ESCALATION) {
        role = "CUSTOMER,CUSTOMER_MANAGER,GXM_ADMIN,GXM_MANAGER,GXM_USER";
      }
      const { users } = await fetchRequestUsers(
        role,
        selectedEntityId,
        selectedServiceLineId,
      );
      const filteredUsers = users.filter((user) => user.id !== loggedInUserId);

      if (reason?.code === "SERVICE_QUERY") {
        const getGxm = filteredUsers?.filter((x) =>
          ["GXM_MANAGER", "GXM_USER"]?.includes(x.role),
        );
        setCC(getGxm);
      }

      setCCUsersData(filteredUsers);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchEntitiesData = async (requestReason = reason) => {
    try {
      const { data } = await fetchEntities();
      setAllEntities(data);
      if (requestType === REQUEST_TYPES.ESCALATION) {
        if (data.length === 1) {
          const availableEntity = data[0];
          setEntity(availableEntity);
          setServiceLineData(availableEntity.services);
          if (availableEntity.services.length === 1) {
            setServiceLine(availableEntity.services[0]);
            fetchAssigneeUsers(
              availableEntity.id,
              availableEntity.services[0].id,
              requestReason,
            );
            fetchCCUser(availableEntity.id, availableEntity.services[0].id);
          }
        }
      } else {
        if (data.length === 1 && requestReason) {
          const availableEntity = data[0];
          setEntity(availableEntity);
          setServiceLineData(availableEntity.services);
          if (availableEntity.services.length === 1) {
            setServiceLine(availableEntity.services[0]);
            fetchAssigneeUsers(
              availableEntity.id,
              availableEntity.services[0].id,
              requestReason,
            );
            fetchCCUser(availableEntity.id, availableEntity.services[0].id);
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const descriptionHandler = (event) => {
    setDescription(event.target.value);
  };

  const handleEntityChange = (e) => {
    setEntity(e);
    setServiceLineData(e.services);
    setAllProcesses([]);
    setSelectedProcess(null);
    setAssignee(null);
    setServiceLine(null);
    setCC(null);
    if (e.services.length === 1) {
      setServiceLine(e.services[0]);
      fetchAssigneeUsers(e.id, e.services[0].id, reason);
      fetchCCUser(e.id, e.services[0].id);
    }
  };

  const handleServiceLineChange = (e) => {
    setServiceLine(e);
    setAllProcesses([]);
    setSelectedProcess(null);
    setAssignee(null);
    setCC(null);
    fetchAssigneeUsers(entity.id, e.id);
    fetchCCUser(entity.id, e.id);
  };

  useEffect(() => {
    if (
      serviceLine &&
      serviceLine.name !== "Payroll +" &&
      requestType === REQUEST_TYPES.ESCALATION &&
      parentTab === REQUEST_TYPES.ESCALATION
    ) {
      fetchProcessesData(entity?.id, serviceLine?.id);
    }
  }, [serviceLine]);

  const fetchProcessesData = async (entityId, serviceLineId) => {
    try {
      const { data } = await apiService(
        API_ROUTES.FETCH_USER_PROCESSES,
        "GET",
        {
          replace: {
            page: 1,
            pageSize: 100,
            sortBy: "name",
            sortOrder: "ASC",
            status: "",
            service: serviceLineId,
            search: "",
          },
          schema: z.object({
            page: z.number().optional(),
            pageSize: z.number().optional(),
            sortBy: z
              .enum(["name", "entity", "service", "due_date"])
              .optional(),
            sortOrder: z.enum(["ASC", "DESC"]).optional(),
            status: z.string().optional(),
            service: z.string().optional(),
            search: z.string().optional(),
          }),
        },
      );
      if (data?.records?.length) {
        const filteredProcesses = data.records
          .filter(
            (process) =>
              process.entity === entityId && process.status !== "completed",
          )
          .map((process) => ({
            ...process,
            displayName: `${process.name} ${getDeadlineDate(process)}${getSequenceNumber(process)}`,
          }));

        const escalatedProcess = filteredProcesses.filter(
          (process) => !process.isEscalated,
        );
        setAllProcesses(filteredProcesses);
        if (escalatedProcess.length === 1) {
          setSelectedProcess(escalatedProcess[0]);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleProcessChange = (process) => {
    if (!process?.isEscalated) {
      setSelectedProcess(process);
    }
  };

  const handleAssigneeChange = (e) => {
    setAssignee(e);
  };

  const handleCCChange = (e) => {
    setCC(e);
  };

  const handleAssigneeRemove = () => {
    handleAssigneeChange(null);
  };

  const handleCCRemove = (userToRemove) => {
    setCC((prevSelected) =>
      prevSelected.filter((user) => user.id !== userToRemove.id),
    );
  };

  const getInitials = (name) => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    return parts.length === 1
      ? parts[0][0].toUpperCase()
      : (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const clear = () => {
    hideRequestModal();
    setReason(null);
    setDescription("");
    setEntity(null);
    setServiceLine(null);
    setAllProcesses([]);
    setSelectedProcess(null);
    setPayrollPlusProcess("");
    setAssignee(null);
    setCC(null);
    setShowClearConfirmation(false);
    setShowAttachmentConfirmation(false);
    setShowSuccess(false);
    setFiles([]);
    fetchEntitiesData(null);
  };

  const handleClear = () => {
    if (
      (requestType === REQUEST_TYPES.AD_HOC &&
        (reason || entity || serviceLine || assignee)) ||
      (requestType === REQUEST_TYPES.ESCALATION &&
        (description ||
          assignee ||
          entity ||
          serviceLine ||
          process ||
          payrollPlusProcess))
    ) {
      setShowClearConfirmation(true);
      hideRequestModal(true);
    } else {
      clear();
    }
  };

  const handleSendRequest = async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      const output = [];
      const attachments = [];
      let tags, metadata;
      let requestBody;
      for (let i = 0; i < files.length; i++) {
        const file = files[i]?.file;
        formData.append("file", file);
        let fileData = {
          fileName: file?.fileName,
          category: file?.category,
          subCategory: file?.subcategory,
        };
        if (file?.description) {
          fileData.description = file?.description;
        }
        output.push(fileData);
      }
      formData.append("filesData", JSON.stringify(output));

      formData.append("description", description || "");

      formData.append(
        "entity",
        requestType === REQUEST_TYPES.AD_HOC ||
          (parentTab === REQUEST_TYPES.ESCALATION &&
            serviceLine.name === "Payroll +")
          ? entity.id
          : process?.entity,
      );
      formData.append(
        "entityName",
        requestType === REQUEST_TYPES.AD_HOC ||
          (parentTab === REQUEST_TYPES.ESCALATION &&
            serviceLine.name === "Payroll +")
          ? entity.name
          : process?.entityData?.name,
      );
      formData.append(
        "country",
        requestType === REQUEST_TYPES.AD_HOC ||
          (parentTab === REQUEST_TYPES.ESCALATION &&
            serviceLine.name === "Payroll +")
          ? entity.country
          : process?.entityData?.country,
      );

      requestType === REQUEST_TYPES.AD_HOC &&
        formData.append("reason", reason.name);

      requestType === REQUEST_TYPES.ESCALATION &&
        ((parentTab === REQUEST_TYPES.ESCALATION &&
          serviceLine.name !== "Payroll +") ||
          parentTab !== REQUEST_TYPES.ESCALATION) &&
        formData.append("process", process.id);

      requestType === REQUEST_TYPES.ESCALATION &&
        parentTab === REQUEST_TYPES.ESCALATION &&
        serviceLine.name === "Payroll +" &&
        formData.append("custom_process_name", payrollPlusProcess);

      requestType === REQUEST_TYPES.AD_HOC &&
        formData.append("service", serviceLine.name);
      requestType === REQUEST_TYPES.AD_HOC &&
        formData.append("serviceId", serviceLine.id);

      formData.append("assignee", assignee?.id);
      formData.append(
        "cc",
        JSON.stringify(cc?.map?.((ccUsers) => ccUsers.id) || []),
      );
      if (requestType === REQUEST_TYPES.AD_HOC) {
        requestBody = {
          entity: entity.id,
          cc: cc?.map?.((ccUsers) => ccUsers.id) || [],
          assignee: assignee?.id,
          reason: reason.name,
          description: description || "",
          service: serviceLine.id,
        };
        tags = {
          entity: uuidToUlid(entity.id),
          service: SERVICE_CODES[serviceLine.name],
          country: entity.country,
          request_reason: REQUEST_REASON_CODES[reason.name],
          year: new Date().getFullYear().toString(),
          temporary_file: "true",
        };
      } else {
        requestBody = {
          entity:
            parentTab === REQUEST_TYPES.ESCALATION &&
            serviceLine.name === "Payroll +"
              ? entity.id
              : process?.entity,
          cc: cc?.map?.((ccUsers) => ccUsers.id) || [],
          ...(((parentTab === REQUEST_TYPES.ESCALATION &&
            serviceLine.name !== "Payroll +") ||
            parentTab !== REQUEST_TYPES.ESCALATION) && { process: process.id }),
          ...(parentTab === REQUEST_TYPES.ESCALATION &&
            serviceLine.name === "Payroll +" && {
              custom_process_name: payrollPlusProcess,
            }),
          service:
            parentTab !== REQUEST_TYPES.ESCALATION
              ? process?.service
              : serviceLine?.id,
          assignee: assignee?.id,
          description: description || "",
        };
        tags = {
          entity:
            parentTab === REQUEST_TYPES.ESCALATION &&
            serviceLine.name === "Payroll +"
              ? uuidToUlid(entity.id)
              : uuidToUlid(process?.entity),
          country:
            parentTab === REQUEST_TYPES.ESCALATION &&
            serviceLine.name === "Payroll +"
              ? entity.country
              : process?.entityData?.country,
          escalated_process:
            parentTab === REQUEST_TYPES.ESCALATION &&
            serviceLine.name === "Payroll +"
              ? ""
              : uuidToUlid(process.id),
          temporary_file: "true",
          year: new Date().getFullYear().toString(),
        };
      }
      for (let i = 0; i < files.length; i++) {
        const file = files[i]?.file;
        const uploadData = await uploadFile(file);
        attachments.push({ url: uploadData.url, fileName: uploadData.id });
      }
      requestBody = { ...requestBody, attachments };
      const { data, status } = await saveRequest(
        JSON.stringify(requestBody),
        requestType,
      );
      if (requestType === REQUEST_TYPES.AD_HOC) {
        tags = { ...tags, request: uuidToUlid(data.id) };
      } else {
        tags = { ...tags, escalation: uuidToUlid(data.id) };
      }
      for (let i = 0; i < files.length; i++) {
        const file = files[i]?.file;
        const url = attachments.find(
          (fileAttachment) => fileAttachment.fileName === file.fileName,
        ).url;
        tags = {
          ...tags,
          category: file?.category,
          subCategory: file?.subcategory,
          temporary_file: "false",
        };
        const version =
          file?.versionNumber.toString() || `${await getFileVersion(tags)}`;
        metadata = {
          original_file_name: encodeURIComponent(file.name),
          file_size: file.size.toString(),
          file_type: file.type,
          user: userId,
          file_name: file.fileName,
          version,
          created_at: new Date().toISOString(),
          ...(file?.description && {
            description: encodeURIComponent(file?.description),
          }),
          file_object_type:
            requestType === REQUEST_TYPES.AD_HOC
              ? REQUEST_TYPES.AD_HOC
              : REQUEST_TYPES.ESCALATION,
        };
        metadata = { ...metadata, url };
        await updateTag(file.fileName, tags, metadata);
      }

      if (status) {
        setFiles([]);
        setRequestId(data.id);
        setShowSuccess(true);
      } else {
        setShowSuccess(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fileSupported = [
    "xls",
    "csv",
    "xml",
    "pdf",
    "jpg",
    "jpeg",
    "png",
    "txt",
    "doc",
    "docx",
    "xlsx",
  ];
  const uploadFileHandler = (file) => {
    setFiles((prevFiles) => [...prevFiles, ...file]);
  };
  const onFileRemove = (fileToRemove) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToRemove));
  };
  const onUpdateVisibility = (fileToRemove) => {
    setFiles((prevFiles) =>
      prevFiles.map((file) =>
        file === fileToRemove
          ? { ...file, error: { state: true, visible: false } }
          : file,
      ),
    );
  };

  const replaceFile = (fileInput, fileToReplace) => {
    let newFile;
    const selectedFile = fileInput.files[0];

    if (!fileSupported.includes(selectedFile.name.split(".").pop())) {
      newFile = { file: selectedFile, error: { state: true, visible: true } };
    } else {
      if (getFileSizeMB(selectedFile) > 20) {
        newFile = { file: selectedFile, error: { state: true, visible: true } };
      } else {
        newFile = {
          file: selectedFile,
          error: { state: false, visible: false },
        };
      }
    }

    setFiles((prevFiles) =>
      prevFiles.map((existingFileObj) =>
        existingFileObj.file === fileToReplace ? newFile : existingFileObj,
      ),
    );
  };
  const getFileSizeMB = (file) => (file.size / (1024 * 1024)).toFixed(2);

  const getReason = () => {
    return {
      ...(requestType === REQUEST_TYPES.AD_HOC && { reason: reason.name }),
      ...(requestType === REQUEST_TYPES.ESCALATION && { reason: "Escalation" }),
    };
  };

  const getDeadlineDate = (process) => {
    if (process?.start_date) {
      const deadline =
        new Date(process?.start_date).getFullYear() +
        new Date(process?.start_date).toLocaleDateString("default", {
          month: "2-digit",
        });
      return deadline;
    }
  };

  const getSequenceNumber = (process) => {
    if (process?.sequence) {
      return `_${process?.sequence}`;
    }
    return "";
  };

  return {
    reason,
    setReason,
    description,
    setDescription,
    entity,
    setEntity,
    serviceLine,
    setServiceLine,
    assignee,
    setAssignee,
    cc,
    setCC,
    process,
    showClearConfirmation,
    setShowClearConfirmation,
    showSuccess,
    setShowSuccess,
    serviceLineData,
    ccRef,
    assigneeUsers,
    allEntities,
    ccUsersData,
    handleReasonChange,
    descriptionHandler,
    handleEntityChange,
    handleAssigneeChange,
    handleServiceLineChange,
    handleCCChange,
    handleCCRemove,
    handleAssigneeRemove,
    getInitials,
    requestId,
    setRequestId,
    isLoading,
    setIsLoading,
    clear,
    handleClear,
    handleSendRequest,
    showUploadModal,
    setShowUploadModal,
    uploadFileHandler,
    fileSupported,
    getReason,
    files,
    onFileRemove,
    onUpdateVisibility,
    replaceFile,
    getFileSizeMB,
    config,
    showAttachmentConfirmation,
    setShowAttachmentConfirmation,
    selectedProcess,
    setSelectedProcess,
    handleProcessChange,
    allProcesses,
    getDeadlineDate,
    getSequenceNumber,
    payrollPlusProcess,
    setPayrollPlusProcess,
    isImplementationRequestEnabled,
    userRole,
  };
};

export default useRequestModal;
